import { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { BtnLoader } from "src/Loader";
import { GRAPHQL_ENDPOINT } from "src/constants/endpoints";
import { PAYMENTS_BY_STORE_AND_PAYMENT_TYPE, REPORTS_SALES_SUMMARY } from "src/constants/queries";
import { useAuth } from "src/contexts/auth-provider";
import { formatCentsToDollarsWithDollarSign } from "src/helpers";
import { ReportPageLayout } from "src/features/Reports/components/ReportPageLayout";
import { useAxiosInterceptor } from "src/hooks/useAxiosInterceptor";
import { restaurantSegment } from "src/utils/restaurantUrl";
import { useRestaurantFromParams } from "src/utils/useRestaurantFromParams";
import { financeUrl, type FinanceOverview, type FinancePaymentAccount, type FinancePayout } from "./financeApi";
import type { PaymentNode } from "./types";

const cardClass = "rounded-lg border border-[#e5e7eb] bg-white p-4 shadow-[0_4px_12px_rgba(0,0,0,0.04)]";
const TABLE_HEAD_CLASS =
  "py-3 px-4 align-middle text-[12px] font-semibold uppercase tracking-wide text-[#374151] border-b border-[#e2e8f0] bg-[#f1f5f9] whitespace-nowrap";
const TABLE_CELL_CLASS = "px-4 py-3 align-middle text-sm text-[#111827] whitespace-nowrap";
const RECENT_PAYMENTS_LIMIT = 10;

interface OverviewRestData {
  paymentAccount: FinancePaymentAccount;
  pendingPayoutAmountCents: number;
  lastPayout?: FinancePayout | null;
}

interface SalesSummary {
  totalGrossCents?: number | null;
}

function startOfTodayIso() {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  return date.toISOString();
}

function endOfTodayIso() {
  const date = new Date();
  date.setHours(23, 59, 59, 999);
  return date.toISOString();
}

function formatPaymentDate(value: number | string | null | undefined) {
  if (value == null || value === "") return "-";
  const numericValue = typeof value === "number" ? value : Number(value);
  const date =
    Number.isFinite(numericValue) && String(value).trim() !== ""
      ? new Date(numericValue < 1_000_000_000_000 ? numericValue * 1000 : numericValue)
      : new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleString();
}

function formatOrderReference(payment: PaymentNode) {
  if (!payment.customerOrderCorrelationId) return "-";
  return `#${payment.customerOrderCorrelationId.slice(0, 5)}`;
}

export function FinanceOverviewPage() {
  const { axBe } = useAxiosInterceptor();
  const { hasRestaurant } = useAuth();
  const { restaurantCorrelationId, redirectTo } = useRestaurantFromParams();
  const [overview, setOverview] = useState<OverviewRestData | null>(null);
  const [todaysSalesCents, setTodaysSalesCents] = useState(0);
  const [recentPayments, setRecentPayments] = useState<PaymentNode[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const storeCorrelationId = hasRestaurant?.correlationId;
  const todayRange = useMemo(() => ({ from: startOfTodayIso(), to: endOfTodayIso() }), []);

  useEffect(() => {
    if (!storeCorrelationId) return;
    setLoading(true);
    setError(null);

    const overviewRequest = axBe
      .get<FinanceOverview>(financeUrl("overview", storeCorrelationId))
      .then((response) => {
        const { paymentAccount, pendingPayoutAmountCents, lastPayout } = response.data;
        setOverview({ paymentAccount, pendingPayoutAmountCents, lastPayout });
      });

    const salesRequest = axBe
      .post<{ data?: { reportsSalesSummary?: SalesSummary }; errors?: Array<{ message?: string }> }>(GRAPHQL_ENDPOINT, {
        query: REPORTS_SALES_SUMMARY,
        variables: {
          input: {
            storeCorrelationId,
            from: todayRange.from,
            to: todayRange.to,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          },
        },
      })
      .then(({ data }) => {
        const err = data?.errors?.[0]?.message;
        if (err) throw new Error(err);
        setTodaysSalesCents(data?.data?.reportsSalesSummary?.totalGrossCents ?? 0);
      });

    const recentPaymentsRequest = axBe
      .post<{
        data?: {
          paymentsByStoreCorrelationIdAndPaymentType?: {
            edges?: Array<{ node: PaymentNode }>;
          };
        };
        errors?: Array<{ message?: string }>;
      }>(GRAPHQL_ENDPOINT, {
        query: PAYMENTS_BY_STORE_AND_PAYMENT_TYPE,
        variables: {
          storeCorrelationId,
          startTimeInEpochMilli: 0,
          endTimeInEpochMilli: Date.now() + 24 * 60 * 60 * 1000,
          paymentType: "CARD",
          first: RECENT_PAYMENTS_LIMIT,
        },
      })
      .then(({ data }) => {
        const err = data?.errors?.[0]?.message;
        if (err) throw new Error(err);
        setRecentPayments(data?.data?.paymentsByStoreCorrelationIdAndPaymentType?.edges?.map((edge) => edge.node) ?? []);
      });

    Promise.all([overviewRequest, salesRequest, recentPaymentsRequest])
      .catch(() => setError("Unable to load finance overview."))
      .finally(() => setLoading(false));
  }, [axBe, storeCorrelationId, todayRange.from, todayRange.to]);

  if (redirectTo) return <Navigate to={redirectTo} replace />;
  if (!storeCorrelationId) return <ReportPageLayout hideTitle>Select a restaurant to view finance.</ReportPageLayout>;

  const segment = restaurantSegment(restaurantCorrelationId ?? storeCorrelationId, hasRestaurant?.name);
  const account = overview?.paymentAccount;

  return (
    <ReportPageLayout hideTitle fillHeight error={error ?? undefined}>
      {loading && !overview ? (
        <div className="flex min-h-[220px] flex-1 items-center justify-center">
          <BtnLoader />
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col gap-4">
          <div className="grid gap-3 md:grid-cols-4">
            <div className={cardClass}>
              <div className="text-xs font-medium text-[#6b7280]">Payment Account</div>
              <div className="mt-2 text-lg font-semibold text-[#111827]">{account?.status ?? "NOT_CONNECTED"}</div>
            </div>
            <div className={cardClass}>
              <div className="text-xs font-medium text-[#6b7280]">Processing</div>
              <div className="mt-2 text-lg font-semibold text-[#111827]">{account?.processingEnabled ? "Enabled" : "Disabled"}</div>
            </div>
            <div className={cardClass}>
              <div className="text-xs font-medium text-[#6b7280]">Settlement</div>
              <div className="mt-2 text-lg font-semibold text-[#111827]">{account?.settlementEnabled ? "Enabled" : "Disabled"}</div>
            </div>
            <div className={cardClass}>
              <div className="text-xs font-medium text-[#6b7280]">Today's Sales</div>
              <div className="mt-2 text-lg font-semibold text-[#111827]">
                {formatCentsToDollarsWithDollarSign(todaysSalesCents)}
              </div>
            </div>
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <div className={cardClass}>
              <div className="text-xs font-medium text-[#6b7280]">Pending Payout Amount</div>
              <div className="mt-2 text-lg font-semibold text-[#111827]">
                {formatCentsToDollarsWithDollarSign(overview?.pendingPayoutAmountCents ?? 0)}
              </div>
            </div>
            <div className={cardClass}>
              <div className="text-xs font-medium text-[#6b7280]">Last Payout</div>
              <div className="mt-2 text-lg font-semibold text-[#111827]">
                {overview?.lastPayout?.netAmountCents != null
                  ? formatCentsToDollarsWithDollarSign(overview.lastPayout.netAmountCents)
                  : "No payouts yet"}
              </div>
            </div>
          </div>

          <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-[#e5e7eb] bg-white shadow-[0_4px_12px_rgba(0,0,0,0.04)]">
            <div className="flex items-center justify-between border-b border-[#e5e7eb] px-4 py-3">
              <h2 className="text-sm font-semibold text-[#111827]">Recent Payments</h2>
              <Link to={`/restaurants/${segment}/finance/payments`} className="text-sm font-medium text-[#FD7657]">
                View all
              </Link>
            </div>
            <div className="min-h-0 flex-1 overflow-auto">
              <table className="w-full min-w-[680px] border-collapse table-auto text-left">
                <thead>
                  <tr>
                    <th className={TABLE_HEAD_CLASS}>Date</th>
                    <th className={TABLE_HEAD_CLASS}>Order</th>
                    <th className={TABLE_HEAD_CLASS}>Amount</th>
                    <th className={TABLE_HEAD_CLASS}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {recentPayments.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-4 py-8 text-center text-sm text-[#6b7280]">
                        No recent transactions.
                      </td>
                    </tr>
                  ) : (
                    recentPayments.map((payment) => (
                      <tr key={payment.correlationId} className="border-t border-[#f1f5f9] hover:bg-[#F9FAFB]">
                        <td className={TABLE_CELL_CLASS}>{formatPaymentDate(payment.paymentDate ?? payment.createdAt)}</td>
                        <td className={TABLE_CELL_CLASS}>{formatOrderReference(payment)}</td>
                        <td className={`${TABLE_CELL_CLASS} font-semibold`}>
                          {formatCentsToDollarsWithDollarSign(payment.paymentAmountCents ?? 0)}
                        </td>
                        <td className={TABLE_CELL_CLASS}>{payment.paymentStatus ?? "-"}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </ReportPageLayout>
  );
}
